import { useEffect, useRef, useState } from 'react';
import type { EditorProps } from './Editors';

// Local text supports multi-digit/negative entry. Python details remain canonical.
function useDirectNumbers(canonical: number[], bounds: [number, number][], disabled: boolean, commit: (values: number[]) => Promise<void>) {
  const [values, setValues] = useState(() => canonical.map(String));
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const current = useRef(canonical); current.current = canonical;
  const local = useRef(values);
  const reset = () => { const next = current.current.map(String); local.current = next; setValues(next); };
  const identity = JSON.stringify(canonical);
  useEffect(reset, [identity]);
  const publish = async () => {
    if (pending.current) return;
    if (disabled) { reset(); return; }
    const numbers = local.current.map(Number);
    if (local.current.some(v => !v.trim()) || numbers.some((v, i) => !Number.isInteger(v) || v < bounds[i][0] || v > bounds[i][1])) { reset(); return; }
    if (numbers.every((v, i) => v === current.current[i])) { reset(); return; }
    pending.current = true; setBusy(true);
    try { await commit(numbers); }
    catch { /* The application presents refusals; inputs return to Python details. */ }
    finally { pending.current = false; setBusy(false); reset(); }
  };
  return (index: number) => ({
    type: 'number' as const, min: bounds[index][0], max: bounds[index][1], value: values[index], disabled, readOnly: busy, 'aria-busy': busy,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      if (pending.current || disabled) return;
      const next = [...local.current]; next[index] = event.target.value; local.current = next; setValues(next);
    },
    onBlur: () => { void publish(); },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') { event.preventDefault(); void publish(); }
      if (event.key === 'Escape') { event.preventDefault(); reset(); }
    },
  });
}

export function InstrumentNumberField({ value, min, max, label, disabled = false, commit }: {
  value: number; min: number; max: number; label: string; disabled?: boolean; commit: (value: number) => Promise<void>;
}) {
  const field = useDirectNumbers([value], [[min, max]], disabled, values => commit(values[0]));
  return <label>{label}<input aria-label={label} {...field(0)} /></label>;
}

export function InstrumentTuningCells({ tuning, register, count, disabled, scaleRoot, apply }: {
  tuning: any; register: number; count: number; disabled: boolean; scaleRoot: number; apply: EditorProps['apply'];
}) {
  const supportedRoot = Number.isInteger(scaleRoot) && scaleRoot >= 1 && scaleRoot <= 128;
  const limit = supportedRoot ? scaleRoot - 1 : 0;
  const field = useDirectNumbers([tuning.octave, tuning.semitone, tuning.fine_cents], [[-6, 6], [-limit, limit], [-99, 99]], disabled || !supportedRoot,
    async ([octave, semitone, fine]) => { await apply('edit-in-register-tuning', { register, octave, semitone, fine }); });
  const name = String.fromCharCode(65 + register);
  return <>{['octave', 'semitone', 'fine tuning'].map((label, index) => <td key={label} rowSpan={count}><input aria-label={`Register ${name} ${label}`} {...field(index)} aria-describedby={!supportedRoot ? "instrument-scale-domain" : undefined} /></td>)}</>;
}

export function validStoredScale(root: number, integer: number, fraction: number) {
  return Number.isInteger(root) && root >= 1 && root <= 255
    && Number.isInteger(integer) && integer >= 1 && integer <= 7
    && Number.isInteger(fraction) && fraction >= 0 && fraction <= 99;
}

// Split decimal text, never multiply or round a binary floating-point value.
export function parseScaleBase(text: string): [number, number] | null {
  const match = /^([1-7])(?:\.([0-9]{1,2}))?$/.exec(text);
  return match ? [Number(match[1]), Number((match[2] ?? '').padEnd(2, '0'))] : null;
}

export function InstrumentScaleFields({ root, integer, fraction, disabled, apply }: {
  root: number; integer: number; fraction: number; disabled: boolean; apply: EditorProps['apply'];
}) {
  const canonical = [String(root ?? '—'), `${integer ?? '—'}.${String(fraction ?? '—').padStart(2, '0')}`];
  const current = useRef({ root, integer, fraction, canonical });
  current.current = { root, integer, fraction, canonical };
  const [values, setValues] = useState(canonical);
  const local = useRef(values);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [invalidInput, setInvalidInput] = useState('');
  const valid = validStoredScale(root, integer, fraction);
  const high = valid && root > 128;
  const reset = () => { local.current = current.current.canonical; setValues(local.current); };
  const identity = JSON.stringify(canonical);
  useEffect(() => { reset(); setInvalidInput(''); }, [identity]);
  const publish = async (index: number) => {
    if (pending.current) return;
    if (disabled || !valid || (index === 1 && high)) { reset(); return; }
    if (index === 0 && local.current[0] === String(current.current.root)) { reset(); return; }
    const base = index === 1 ? parseScaleBase(local.current[1]) : [current.current.integer, current.current.fraction];
    const nextRoot = index === 0 && /^[0-9]+$/.test(local.current[0]) ? Number(local.current[0]) : index === 1 ? current.current.root : NaN;
    if (!base || !Number.isInteger(nextRoot) || nextRoot < 1 || nextRoot > 128) {
      setInvalidInput(index === 0 ? 'Scale Root must be a whole number from 1 to 128.' : 'Scale Base must be 1–7 with up to two decimal digits.'); reset(); return;
    }
    setInvalidInput('');
    if (nextRoot === current.current.root && base[0] === current.current.integer && base[1] === current.current.fraction) { reset(); return; }
    pending.current = true; setBusy(true);
    try { await apply('edit-in-scale', { root: nextRoot, baseInteger: base[0], baseFraction: base[1] }); }
    catch { /* The application presents the refusal; Python details remain canonical. */ }
    finally { pending.current = false; setBusy(false); reset(); }
  };
  const field = (index: number) => ({
    value: values[index], disabled: disabled || !valid || (index === 1 && high), readOnly: busy, 'aria-busy': busy,
    'aria-describedby': !valid || high ? 'instrument-scale-domain' : undefined,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      if (pending.current) return;
      const next = [...local.current]; next[index] = event.target.value; local.current = next; setValues(next); setInvalidInput('');
    },
    onBlur: () => { void publish(index); },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') { event.preventDefault(); void publish(index); }
      if (event.key === 'Escape') { event.preventDefault(); reset(); setInvalidInput(''); }
    },
  });
  return <section aria-label="Scale" className="instrument-scale"><h4>Scale</h4><div className="inline-fields">
    <label>Root<input aria-label="Scale Root" type="number" min="1" max="128" {...field(0)} /></label>
    <label>Base<input aria-label="Scale Base" type="text" inputMode="decimal" {...field(1)} /></label>
  </div>
    {!valid && <p id="instrument-scale-domain" role="note" className="microcopy">Stored Scale metadata is invalid. Values are preserved; Scale and register tuning cannot be edited.</p>}
    {high && <p id="instrument-scale-domain" role="note" className="microcopy">Stored Root {root} is preserved but outside the supported authoring range. Change Root to 1–128 to enable Scale and register tuning.</p>}
    {invalidInput && <p role="alert" className="microcopy">{invalidInput}</p>}
  </section>;
}
